import { useState, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useSalesSummaryQuery } from "../api/queries";
import { SalesReportsSummaryCards } from "../ui/sales/SalesReportsSummaryCards";
import { SalesReportsContent } from "../ui/sales/SalesReportsContent";
import { SalesReportsFilter } from "../ui/sales/SalesReportsFilter";
import { TrendingUpIcon, FileDownIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";


import type { SalesSummaryParams, SalesByDayParams } from "../api/client";
import { reportsApi } from "../api/client";
import type { SalesByDayItem } from "../../domain/repo/get-sales-by-day";
import type { SalesByBranchItem } from "../../domain/repo/get-sales-by-branch";

function exportSalesToCsv(
  salesByDay: SalesByDayItem[],
  salesByBranch: SalesByBranchItem[],
  groupBy: "day" | "month",
) {
  // Sales by Day/Month section
  const dayHeaders = [
    groupBy === "day" ? "ວັນທີ" : "ເດືອນ",
    "ລາຍໄດ້ (LAK)",
    "ລາຍໄດ້ກ່ອນຫັກ (LAK)",
    "ສ່ວນຫຼຸດ (LAK)",
    "ຈ່າຍແລ້ວ (LAK)",
  ];
  
  const dayRows = salesByDay.map((item) => [
    item.date,
    String(item.revenue),
    String(item.subtotal),
    String(item.discount),
    String(item.paidTotal),
  ]);

  // Sales by Branch section
  const branchHeaders = [
    "ສາຂາ",
    "ລາຍໄດ້ (LAK)",
    "ຈຳນວນອໍເດີ",
    "ຄ່າເຊື່ອຍະສະເລ່ຍ (LAK)",
  ];

  const branchRows = salesByBranch.map((item) => [
    item.branchName,
    String(item.revenue),
    String(item.orderCount),
    String(item.avgOrderValue),
  ]);

  // Combine sections with empty line separator
  const csvContent = [
    "=== ການຂາຍຕາມ" + (groupBy === "day" ? "ວັນ" : "ເດືອນ") + " ===",
    dayHeaders.join(","),
    ...dayRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
    "",
    "=== ການຂາຍຕາມສາຂາ ===",
    branchHeaders.join(","),
    ...branchRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10);
  a.download = `sales-report-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function SalesReportsPage() {
  // Default to last 30 days - use useMemo to prevent infinite loops
  const defaultFilters = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    
    return {
      fromDate,
      toDate,
      statusFilter: "completed" as const,
    };
  }, []);

  const [filters, setFilters] = useState<SalesSummaryParams>(defaultFilters);
  const [groupBy, setGroupBy] = useState<"day" | "month">("day");
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useSalesSummaryQuery(filters);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportParams: SalesByDayParams = {
        ...filters,
        groupBy,
      };
      const result = await reportsApi.getSalesExport(exportParams);
      
      if (
        result.salesByDay.length === 0 &&
        result.salesByBranch.length === 0
      ) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }

      exportSalesToCsv(result.salesByDay, result.salesByBranch, groupBy);
      const totalItems =
        result.salesByDay.length + result.salesByBranch.length;
      toast.success(
        `ສົ່ງອອກ ${totalItems.toLocaleString("lo-LA")} ລາຍການແລ້ວ`,
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍງານການຂາຍ",
              to: "/app/reports/sales",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <TrendingUpIcon className="size-5" />
              ລາຍງານການຂາຍ
            </h2>
            <p className="text-muted-foreground">
              ເບິ່ງປະສິດທິພາບການຂາຍ ແລະ ວິເຄາະເງິນກຳໄລສຸດຍອດ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <FileDownIcon className="size-4" />
            {isExporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ (CSV)"}
          </Button>
        </div>

        <SalesReportsFilter filters={filters} onFiltersChange={setFilters} />

        <div className="mb-4">
          <SalesReportsSummaryCards
            totalRevenue={data?.totalRevenue ?? 0}
            totalOrders={data?.totalOrders ?? 0}
            avgOrderValue={data?.avgOrderValue ?? 0}
            grossMargin={data?.grossMargin ?? 0}
            topBranchName={data?.topBranchName ?? null}
            isLoading={isLoading}
          />
        </div>

        <SalesReportsContent
          filters={filters}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
        />
      </Main>
    </>
  );
}
